import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, articles, faqs, siteInfo } from '@/mocks/siteData';

function SEOHead() {
  return (
    <>
      <title>传教士体位完全指南 | 技巧、变式与深度解析</title>
      <meta name="description" content="全面解析传教士体位的历史起源、基本姿势、进阶技巧与常见变式，帮助伴侣提升亲密关系质量。科学视角，专业指导，让你深入了解传教士体位的每一个细节与实用建议。" />
      <meta name="keywords" content="传教士体位,传教士体位技巧,传教士体位变式,传教士体位角度调整,亲密关系体位指南,传教士体位历史起源,传教士体位进阶玩法" />
      <link rel="canonical" href="https://position-guide.com/" />
      <meta property="og:title" content="传教士体位完全指南 | 技巧、变式与深度解析" />
      <meta property="og:description" content="全面解析传教士体位的历史起源、基本姿势、进阶技巧与常见变式。科学视角，专业指导。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://position-guide.com/" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="传教士体位完全指南 | 技巧、变式与深度解析" />
      <meta name="twitter:description" content="全面解析传教士体位的历史起源、基本姿势、进阶技巧与常见变式。科学视角，专业指导。" />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "传教士体位完全指南",
          "url": "https://position-guide.com/",
          "description": "全面解析传教士体位的历史起源、基本姿势、进阶技巧与常见变式，帮助伴侣提升亲密关系质量。",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://position-guide.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "体位指南",
          "url": "https://position-guide.com/",
          "description": "国内领先的亲密关系知识平台",
          "foundingDate": "2020",
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "contact@position-guide.com",
            "contactType": "customer service"
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "传教士体位完全指南 - 首页",
          "description": "传教士体位完全指南首页，提供历史起源、基本技巧、进阶变式、角度调整等全方位知识内容。",
          "url": "https://position-guide.com/"
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": f.answer
            }
          }))
        })}
      </script>
    </>
  );
}

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredArticles = articles.slice(0, 6);

  return (
    <>
      <SEOHead />
      <ScrollToTop />
      <main>
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20art%20with%20flowing%20organic%20shapes%20in%20cream%20and%20soft%20terracotta%20tones%2C%20gentle%20gradients%2C%20elegant%20minimalist%20composition%2C%20serene%20atmosphere%20with%20delicate%20light%20rays%2C%20fine%20art%20style%2C%20high%20detail%2C%20artist%20photography&width=1600&height=900&seq=home-hero-2026&orientation=landscape"
            alt="体位指南"
            className="absolute inset-0 w-full h-full object-cover"
            title="传教士体位完全指南"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white font-heading mb-4 leading-tight">
              {siteInfo.fullName}
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
              {siteInfo.tagline}<br />
              {siteInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/category/jichu-jiqiao" className="px-6 py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                开始学习
              </Link>
              <Link to="/category/jinjie-bianshi" className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-md text-sm font-medium hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap">
                进阶变式
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">探索知识分类</h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">从基础到进阶，五个维度全面掌握传教士体位的方方面面</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-5 text-center transition-all duration-300 hover:border-primary-300 hover:bg-primary-50/50 cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 hidden sm:block">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">精选文章</h2>
                <p className="text-sm text-foreground-500">由专家团队精心编撰的科学指南</p>
              </div>
              <Link to="/category/jichu-jiqiao" className="hidden sm:flex items-center gap-1 text-sm text-primary-500 font-medium hover:text-primary-600 cursor-pointer whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredArticles.map(article => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Link to="/category/jichu-jiqiao" className="inline-flex items-center gap-1 text-sm text-primary-500 font-medium cursor-pointer">
                查看全部文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">常见问题</h2>
              <p className="text-sm text-foreground-500">关于传教士体位的常见疑问解答</p>
            </div>
            <div className="flex flex-col gap-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-background-200/70 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer bg-background-50 hover:bg-background-100 transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <i className={`text-foreground-400 transition-transform duration-200 text-sm ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-950 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-heading mb-4">准备好深入了解了吗？</h2>
            <p className="text-sm text-background-400 mb-8 max-w-lg mx-auto leading-relaxed">
              浏览我们的完整指南，从基础到进阶，科学视角帮你提升亲密关系质量。
            </p>
            <Link to="/category/jichu-jiqiao" className="inline-block px-8 py-3 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              开始探索
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}